import Tooltip from 'components/@extended/Tooltip';
import CancelButton from 'components/buttons/CancelButton';
import DeleteButton from 'components/buttons/DeleteButton';
import SaveButton from 'components/buttons/SaveButton';
import { useCreateDialogForm, useCreateRoute } from 'components/create-dialog/CreateDialogContext';
import { useCreateDraft } from 'components/create-dialog/useCreateDraft';
import { DraftSaveMode } from 'components/drafts/DraftSaveMode';
import ActionHeader from 'components/forms/ActionHeader.tsx';
import FormWrapper from 'components/forms/form-components/FormWrapper';
import CustomFields from 'components/forms/inputs/custom-fields/CustomFields';
import InfoTooltip from 'components/InfoTooltip';
import Loader from 'components/Loader';
import ConfirmNavigation from 'components/navigation/ConfirmNavigation';
import Section from 'components/section-menu/Section';
import { HighlightedFieldsProvider, SectionActionsProvider } from 'components/section-menu/SectionActionsContext';
import SectionLayout from 'components/section-menu/SectionLayout';
import { useSectionErrors } from 'components/section-menu/useSectionErrors';
import useFocusedEditMode from 'hooks/useFocusedEditMode';
import { useRef } from 'react';
import { Control, FieldValues, FormProvider } from 'react-hook-form';
import { useSelector } from 'store';
import { useAddJobRoleMutation, useUpdateJobRoleMutation } from 'store/api/jobRoles';
import { ClassifierData } from 'types/classifiers';
import { CustomField } from 'types/customFields';
import { JobRoleDetailsDto, JobRoleForm } from 'types/jobRoles';
import { errorAlert } from 'utils/alert';
import { t } from 'utils/getTranslations';
import { useHasPermissions } from 'utils/permissionUtil.ts';
import { useGoBack, useLeaveForm, useReturnToLocation } from 'utils/useNavigateWithReturnState';
import { JobRoleFields, JobRoleSettingsFields, JobRoleTranslationsFields } from './form-sections/JobRoleFields';
import { useJobRoleForm } from './form-sections/useJobRoleForm';
import { jobRoleFormToRequest } from './jobRoleFormMapping';
import { useDeleteJobRole } from './useDeleteJobRole';

type Props = {
    type: 'add' | 'edit';
    initialData?: JobRoleForm;
    classifiers: Record<string, ClassifierData>;
    refetch?: () => void;
    readOnlyExistingJobRole?: JobRoleDetailsDto;
    entityCustomFields?: CustomField[];
};

const FIELD_TO_SECTION_MAP: Record<string, string> = {
    orderNum: 'job-roles.job-role',
    name: 'job-roles.job-role',
    internalName: 'job-roles.job-role',
    abbreviation: 'job-roles.job-role',
    groupNameType: 'job-roles.job-role',
    isActive: 'job-roles.job-role',
    excludeFromMinmaxCalculation: 'job-roles.job-role',
    entityTexts: 'job-roles.public-name-translations',
};

const JobRolePageForm = ({
    type = 'add',
    initialData,
    classifiers,
    refetch,
    readOnlyExistingJobRole,
    entityCustomFields,
}: Props): JSX.Element => {
    useFocusedEditMode('job-roles');
    const { id: jobRoleId } = useCreateRoute().params;
    const hasPermissionToDelete = useHasPermissions(['DELETE.JOB_ROLES']);
    const returnTo = useReturnToLocation(jobRoleId ? `/job-role/${jobRoleId}` : '/job-roles');
    const goBack = useGoBack(returnTo);
    const leaveForm = useLeaveForm();

    const form = useJobRoleForm({ initialData, classifiers, entityCustomFields });
    const { methods, customFields, mapCustomFieldsToDto } = form;
    const toRequest = (data: JobRoleForm) =>
        jobRoleFormToRequest(data, mapCustomFieldsToDto(data as unknown as Record<string, unknown>));
    useCreateDialogForm(methods);
    const draft = useCreateDraft({ record: 'job-role', methods, toPayload: toRequest });
    const { handleSubmit, control, reset, formState } = methods;
    const { errors } = formState;

    const sectionErrors = useSectionErrors(errors, FIELD_TO_SECTION_MAP);

    const [addJobRole] = useAddJobRoleMutation();
    const [updateJobRole] = useUpdateJobRoleMutation();

    const bypassNavigationBlockerRef = useRef(false);
    const handleDeleteJobRole = useDeleteJobRole(jobRoleId, () => {
        bypassNavigationBlockerRef.current = true;
        reset({}, { keepValues: true });
    });

    const onSubmit = async (formData: JobRoleForm, action: 'create' | 'update'): Promise<void> => {
        try {
            const data = toRequest(formData);
            if (action === 'create') {
                // A draft is created by completing it: the job role keeps the draft's id.
                await (draft.draftId === null ? addJobRole(data).unwrap() : draft.complete(data))
                    .then((resp) => {
                        bypassNavigationBlockerRef.current = true;
                        reset({}, { keepValues: true });
                        leaveForm(`/job-role/${resp.id}`);
                    })
                    .catch(console.error);
            } else if (action === 'update' && jobRoleId && !isNaN(Number(jobRoleId))) {
                await updateJobRole({
                    ...data,
                    id: jobRoleId,
                })
                    .unwrap()
                    .then(() => {
                        bypassNavigationBlockerRef.current = true;
                        if (refetch) refetch();
                        reset({}, { keepValues: true });
                        leaveForm(`/job-role/${jobRoleId}`);
                    })
                    .catch(console.error);
            }
        } catch (e) {
            console.error('onSubmit error: ', e);
            errorAlert(t('forms.error-submitting-the-form'));
        }
    };

    const submitCreate = () => {
        handleSubmit((data) => onSubmit(data, 'create'))();
    };

    const submitUpdate = () => {
        handleSubmit((data) => onSubmit(data, 'update'))();
    };

    const isDeleteRestricted = !!readOnlyExistingJobRole?.jobRoleEmployees?.length && hasPermissionToDelete;
    const deleteButton = (
        <DeleteButton
            key="editDeleteJobRoles"
            permissions={['DELETE.JOB_ROLES']}
            disabled={isDeleteRestricted}
            onClick={handleDeleteJobRole}
        />
    );

    // Show inital name if is edit form. Dont do dynamically to prevent unneeded rerenders
    const getFormTitle = () => {
        switch (type) {
            case 'edit':
                return initialData?.name;
            default:
                return t('job-roles.add-new-job-role');
        }
    };

    const { buttonsDisabled } = useSelector((state) => state.buttons);

    if (form.isLoadingCustomFieldData) {
        return <Loader />;
    }

    const getFormHeader = () => {
        const actions: JSX.Element[] = [<CancelButton key="cancel" onClick={goBack} />];

        if (type === 'add') {
            actions.push(
                ...(draft.button ? [draft.button] : []),
                <SaveButton
                    key="addSave"
                    permissions={['CREATE.JOB_ROLES']}
                    onClick={submitCreate}
                    disabled={buttonsDisabled}
                />
            );
        } else if (type === 'edit' && jobRoleId) {
            actions.push(
                isDeleteRestricted ? (
                    <Tooltip key="editDeleteJobRoles" text="job-roles.delete-restriction-text">
                        {/* The span is the hover target: a disabled button fires no pointer events of its own. */}
                        <span className="inline-flex">{deleteButton}</span>
                    </Tooltip>
                ) : (
                    deleteButton
                ),
                <SaveButton
                    key="editSave"
                    permissions={['UPDATE.JOB_ROLES']}
                    onClick={submitUpdate}
                    disabled={buttonsDisabled}
                />
            );
        }

        return <ActionHeader title={getFormTitle()} actions={actions} onBack={goBack} draft={draft.detail} />;
    };

    return (
        <FormProvider {...methods}>
            <SectionActionsProvider>
                <HighlightedFieldsProvider>
                    <FormWrapper>
                        {getFormHeader()}
                        <DraftSaveMode mode={draft.mode}>
                            <SectionLayout showMenu={false} sectionErrors={sectionErrors}>
                                <Section title="job-roles.job-role">
                                    <JobRoleFields form={form} />
                                    <JobRoleSettingsFields form={form} />
                                </Section>
                                <Section
                                    title="job-roles.public-name-translations"
                                    tooltip={<InfoTooltip text="translations.mandatory-fields" />}
                                >
                                    <JobRoleTranslationsFields form={form} />
                                </Section>
                                {customFields.length > 0 ? (
                                    <CustomFields
                                        control={control as unknown as Control<FieldValues>}
                                        fields={customFields}
                                    />
                                ) : null}
                            </SectionLayout>
                        </DraftSaveMode>
                        <ConfirmNavigation
                            control={control}
                            bypassRef={bypassNavigationBlockerRef}
                            draft={type === 'add' ? draft.leave : null}
                        />
                    </FormWrapper>
                </HighlightedFieldsProvider>
            </SectionActionsProvider>
        </FormProvider>
    );
};

export default JobRolePageForm;
